import 'client-only';
import { useAtom } from 'jotai';
import { type MutableRefObject, useCallback, useEffect } from 'react';

import { PaymentsState } from '@/atoms/payments/paymentsState';
import { type BrowserPaymentRequestType } from '@/components/payments/hooks/useBrowserPayment';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { type BrowserPaymentButtonStyles } from '@/types/payments/types';

/**
 * Mounts an Apple (or Google) Pay Button to the dom via a constant input element ID. The tree
 * should contain one element with the input id, and it's parent should have the container id.
 *
 * We use stripe to render and process Apple Pay. Wrap the page in the StripeClientProvider
 * to ensure the Stripe client context is loaded.
 *
 * @prop paymentRequest The payment request object for Browser payments.
 * @prop onSuccess Executed on the token response.
 * @prop onClick Optional. Executed when the button is clicked.
 * @prop buttonStyleOptions Optional. Overrides any default options.
 */
export const useBrowserPaymentButton = ({
  paymentRequest,
  onSuccess,
  onClick = async () => {},
  buttonStyleOptions = {},
}: {
  paymentRequest: MutableRefObject<BrowserPaymentRequestType>;
  onSuccess: (
    token: stripe.paymentRequest.StripeTokenPaymentResponse,
  ) => Promise<void>;
  onClick?: (event: { preventDefault: () => void }) => Promise<void>;
  buttonStyleOptions?: Partial<BrowserPaymentButtonStyles>;
}) => {
  const stripeClient = useStripeClient();
  const [stripeElement, setStripeElement] = useAtom(
    PaymentsState.StripeElementAtom,
  );

  const isButtonLoaded = (buttonId: string): boolean =>
    !!document.getElementById(buttonId)?.hasChildNodes();

  const createNewButton = useCallback(
    (
      elements: stripe.elements.Elements,
      options: stripe.elements.ElementsOptions,
    ) => {
      if (stripeElement.paymentRequestButton) {
        stripeElement.paymentRequestButton.destroy();
      }

      const paymentRequestButton = elements.create(
        'paymentRequestButton',
        options,
      );

      setStripeElement((prev) => ({ ...prev, paymentRequestButton }));

      return paymentRequestButton;
    },
    [setStripeElement, stripeElement.paymentRequestButton],
  );

  useEffect(() => {
    const elements = stripeClient?.elements();
    if (
      elements &&
      !!paymentRequest.current &&
      !isButtonLoaded(paymentRequest.current.buttonId)
    ) {
      const paymentRequestButton = createNewButton(elements, {
        paymentRequest: paymentRequest.current.paymentRequest,
        style: {
          paymentRequestButton: {
            borderRadius: '26px',
            height: '52px',
            theme: 'dark',
            type: 'default',
            ...buttonStyleOptions,
          },
        },
      });

      paymentRequestButton.mount(`#${paymentRequest.current.buttonId}`);

      paymentRequestButton.on('click', async (event) => {
        await onClick(event);
      });
    }

    if (paymentRequest.current) {
      paymentRequest.current.registerCallbacks(onSuccess);
    }
  }, [
    stripeClient,
    buttonStyleOptions,
    onClick,
    onSuccess,
    paymentRequest,
    createNewButton,
  ]);

  return {
    showButtonLoader: !stripeElement.paymentRequestButton,
    buttonId: paymentRequest.current?.buttonId || '',
    buttonContainerId: paymentRequest.current?.buttonContainerId || '',
  };
};
